import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CampaignBlueprintsContext = createContext(null);

export function CampaignBlueprintsProvider({ children }) {
  const { user } = useAuth();
  const [blueprintsByProject, setBlueprintsByProject] = useState({});
  const [limitsByProject, setLimitsByProject] = useState({});
  const [loading, setLoading] = useState(false);

  const refreshBlueprints = useCallback(async (projectId) => {
    if (!projectId || !user) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/campaign-blueprints`);
      setBlueprintsByProject((prev) => ({ ...prev, [projectId]: data.blueprints || [] }));
      setLimitsByProject((prev) => ({ ...prev, [projectId]: data.limits || {} }));
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createBlueprint = useCallback(async (projectId, body) => {
    const { data } = await api.post(`/projects/${projectId}/campaign-blueprints`, body);
    await refreshBlueprints(projectId);
    return data;
  }, [refreshBlueprints]);

  const deleteBlueprint = useCallback(async (projectId, blueprintId) => {
    await api.delete(`/campaign-blueprints/${blueprintId}`);
    setBlueprintsByProject((prev) => ({
      ...prev,
      [projectId]: (prev[projectId] || []).filter((b) => b.id !== blueprintId),
    }));
  }, []);

  const value = useMemo(() => ({
    blueprintsByProject,
    limitsByProject,
    loading,
    refreshBlueprints,
    createBlueprint,
    deleteBlueprint,
  }), [blueprintsByProject, limitsByProject, loading, refreshBlueprints, createBlueprint, deleteBlueprint]);

  return <CampaignBlueprintsContext.Provider value={value}>{children}</CampaignBlueprintsContext.Provider>;
}

export function useCampaignBlueprints() {
  const ctx = useContext(CampaignBlueprintsContext);
  if (!ctx) throw new Error("useCampaignBlueprints must be inside CampaignBlueprintsProvider");
  return ctx;
}
